import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/data';

const ARTICLES_PER_PAGE = 6;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  const currentCategory = useMemo(() => {
    return categories.find(c => c.slug === slug);
  }, [slug]);

  const filteredArticles = useMemo(() => {
    if (!currentCategory) return [];
    return articles.filter(a => a.category === currentCategory.id);
  }, [currentCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE));

  const pagedArticles = useMemo(() => {
    const start = (page - 1) * ARTICLES_PER_PAGE;
    return filteredArticles.slice(start, start + ARTICLES_PER_PAGE);
  }, [filteredArticles, page]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory?.name || '分类' },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {currentCategory ? (
              <>
                <div className="mb-10">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className={`${currentCategory.icon} text-lg`}></i>
                    </span>
                    <div>
                      <h1 className="text-2xl md:text-3xl font-heading font-black text-foreground-950">
                        {currentCategory.name}
                      </h1>
                      <p className="text-sm text-foreground-500 mt-0.5">{currentCategory.description}</p>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-400">
                    共 <strong className="text-primary-500">{filteredArticles.length}</strong> 篇文章
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-background-200">
                  <span className="text-xs text-foreground-500 mr-2 whitespace-nowrap">切换分类：</span>
                  {categories.map(cat => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      onClick={() => setPage(1)}
                      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        cat.slug === slug
                          ? 'bg-foreground-950 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      <i className={`${cat.icon} text-xs`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>

                {filteredArticles.length === 0 ? (
                  <div className="text-center py-20">
                    <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                    <p className="mt-4 text-foreground-500">该分类暂无文章</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                      {pagedArticles.map(article => (
                        <Link
                          key={article.id}
                          to={`/article/${article.id}`}
                          className="group block rounded-2xl bg-background-100 overflow-hidden hover:bg-background-200/70 transition-colors duration-300 cursor-pointer"
                        >
                          <div className="aspect-[16/10] overflow-hidden">
                            <img
                              src={article.coverImage}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-4 md:p-5">
                            <div className="flex items-center justify-between mb-2">
                              <span className="px-2 py-0.5 bg-primary-50 text-primary-600 text-xs font-medium rounded-full">
                                {article.categoryName}
                              </span>
                              <span className="text-xs text-foreground-400">{article.publishDate}</span>
                            </div>
                            <h2 className="text-sm md:text-base font-bold text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors duration-200">
                              {article.title}
                            </h2>
                            <p className="text-xs md:text-sm text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                              {article.summary}
                            </p>
                            <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                              <span className="flex items-center gap-1">
                                <i className="ri-eye-line"></i>
                                {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                              </span>
                              <span className="flex items-center gap-1">
                                <i className="ri-fire-line text-primary-500"></i>
                                {article.hot}°热度
                              </span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-10">
                        <button
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
                        >
                          <i className="ri-arrow-left-s-line"></i>
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                              p === page
                                ? 'bg-foreground-950 text-background-50'
                                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                            }`}
                          >
                            {p}
                          </button>
                        ))}
                        <button
                          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                          className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200 cursor-pointer"
                        >
                          <i className="ri-arrow-right-s-line"></i>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
                <p className="mt-4 text-foreground-500">未找到该分类</p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      {currentCategory && (
        <>
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": `${currentCategory.name} - 51黑料网`,
              "description": currentCategory.description,
              "url": `https://www.51heiliaobaike.com/category/${currentCategory.slug}`,
              "isPartOf": {
                "@type": "WebSite",
                "name": "51黑料网",
                "url": "https://www.51heiliaobaike.com/"
              }
            })
          }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://www.51heiliaobaike.com/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": currentCategory.name,
                  "item": `https://www.51heiliaobaike.com/category/${currentCategory.slug}`
                }
              ]
            })
          }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              "itemListElement": pagedArticles.map((a, idx) => ({
                "@type": "ListItem",
                "position": idx + 1,
                "item": {
                  "@type": "Article",
                  "name": a.title,
                  "url": `https://www.51heiliaobaike.com/article/${a.id}`,
                  "description": a.summary
                }
              })),
              "numberOfItems": filteredArticles.length
            })
          }} />
        </>
      )}
    </>
  );
}